import get from 'lodash/get';
import has from 'lodash/has';
import cloneDeep from 'lodash/cloneDeep';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import max from 'lodash/max';
import { BuildingBlock, BuildingBlockLayouts } from '@pendo/services/BuildingBlocks';
import { setNewModuleStyles } from './set-new-module-styles';
import { getGuideStepsContent } from '@/guides/helpers/common.util';
import { buildIntegrationSandboxCode } from './integration-utils';
import sharedServices from '@pendo/services/package.json';
import {
    createStepId,
    createModuleId,
    createResourceCenterModule,
    addModuleToResourceCenter,
} from '@/utils/resource-center-helper.js';
import { v4 as uuid } from 'uuid';
import { EDITOR_TYPES } from '@pendo/services/Constants.js';
import { combineContent, generateCspContent } from '@/guides/helpers/guide-content';

// Aliasing moduleIds to moduleTypes because that more closely describes what is stored in
// the object. It also helps to distinguish these values from places in the code where we are actually
// creating and using a true unique module id
const { moduleIds: moduleTypes } = BuildingBlockLayouts;

export const guidePatchFields = ['attributes', 'audience', 'audienceUiHint', 'launchMethod', 'state', 'name'];

export const stepPatchFields = [
    'buildingBlocks',
    'dom',
    'content',
    'contentJs',
    'contentCss',
    'attributes',
    'elementPathRule',
    'pageId',
];

function getFrameColor (buildingBlocks) {
    const titleContainer = BuildingBlock.findBlockByDomId(
        buildingBlocks,
        'pendo-resource-center-module-title-container',
    );
    const frameColorProperty = titleContainer.properties.find((property) => property.name === 'frameColor');

    return get(frameColorProperty, 'value', null);
}

function getFrameTop (buildingBlocks) {
    const titleContainer = BuildingBlock.findBlockByDomId(
        buildingBlocks,
        'pendo-resource-center-module-title-container',
    );
    const frameWidthProperty = titleContainer.properties.find((property) => property.name === 'frameTop');

    return get(frameWidthProperty, 'value', null);
}

function getWidthProperty (buildingBlocks) {
    const widthProperty = buildingBlocks.properties.find((property) => property.name === 'layout_width');

    return widthProperty.value;
}

function setModuleName (resourceCenterModule, moduleType, moduleConfig) {
    switch (moduleType) {
        case moduleTypes.guideList:
            resourceCenterModule.name = 'Guide List Module';
            break;
        case moduleTypes.onboarding:
            resourceCenterModule.name = 'Onboarding Module';
            break;
        case moduleTypes.sandbox:
            resourceCenterModule.name = 'Sandbox Module';
            break;
        case moduleTypes.announcements:
            resourceCenterModule.name = 'Announcements';
            break;
        case moduleTypes.iframe:
            resourceCenterModule.name = 'External Website';
            break;
        case moduleTypes.vocPortal:
            resourceCenterModule.name = 'Listen Visitor Portal';
            break;
        case moduleTypes.integration:
            resourceCenterModule.name = moduleConfig.integrationDisplayName
                ? moduleConfig.integrationDisplayName
                : moduleConfig.title;
            resourceCenterModule.attributes.resourceCenter.integrationName = moduleConfig.integrationName;
            resourceCenterModule.attributes.resourceCenter.integrationDisplayName = moduleConfig.integrationDisplayName;
            resourceCenterModule.attributes.resourceCenter.integrationProvider = moduleConfig.integrationProvider;
            break;
    }
}

function setBuildingBlocksAndDom (resourceCenterModule, moduleType, buildingBlocks, buildingBlocksForExistingModules) {
    const { createResourceCenterBuildingBlocksForLayout } = BuildingBlockLayouts;

    const blockProps = {
        frameColor: getFrameColor(buildingBlocks),
        frameTop: getFrameTop(buildingBlocks),
        moduleTitle: resourceCenterModule.name,
        width: getWidthProperty(buildingBlocks),
        iframeSrc: '',
    };

    if (moduleType === moduleTypes.vocPortal) {
        moduleType = moduleTypes.iframe;
        blockProps.width = '100%';
    }

    const moduleBuildingBlocks = createResourceCenterBuildingBlocksForLayout(
        moduleType,
        resourceCenterModule.steps[0].id,
        blockProps,
    );

    setNewModuleStyles(buildingBlocks, moduleBuildingBlocks);
    BuildingBlock.copyContentFromExistingModulesAndHomeView({
        buildingBlocksForNewModule: moduleBuildingBlocks,
        buildingBlocksForExistingModules,
        buildingBlocksForHomeView: buildingBlocks,
    });

    resourceCenterModule.steps[0].buildingBlocks = JSON.stringify(moduleBuildingBlocks);
    resourceCenterModule.steps[0].dom = JSON.stringify(BuildingBlock.buildingBlocksToDom(moduleBuildingBlocks));
}

function addIntegrationSandboxCode (moduleStep, moduleConfig) {
    const integrationContainerId = `integration-container-${uuid()}`;
    const content = buildIntegrationSandboxCode(integrationContainerId, moduleConfig);

    return combineContent(content).then((combinedContent) => {
        const step = moduleStep;
        step.content = combinedContent;
        Object.assign(step, {
            ...generateCspContent(step, combinedContent),
        });

        return step;
    });
}

function setNotificationBubble (draftHomeViewClone, buildingBlocks) {
    const notificationBubble = get(draftHomeViewClone, 'attributes.notificationBubble');
    if (!notificationBubble) {
        const notificationBubbleConfig = {
            'background-color': getFrameColor(buildingBlocks),
            'font-weight': 400,
            'font-family': 'inherit',
            // eslint-disable-next-line internal-rules/no-hardcoded-colors
            'color': '#FFFFFF',
        };
        draftHomeViewClone.attributes.notificationBubble = notificationBubbleConfig;
    }
}

function addStepContent (draftHomeView) {
    if (has(draftHomeView, 'steps[0].buildingBlocks') && has(draftHomeView, 'steps[0].dom')) {
        return Promise.resolve(draftHomeView.steps[0]);
    }

    const draftHomeViewStepsClone = cloneDeep(draftHomeView.steps);

    return getGuideStepsContent(draftHomeViewStepsClone).then((stepsWithContent) => stepsWithContent[0]);
}

export function synchronizeModuleWithHomeView (homeView, module) {
    // RC's that have never turned this flag on will not have this key, after it has been set once it will be a Boolean.
    const doNotResume = get(homeView, 'attributes.doNotResume');

    // Don't bother setting the value if its not a Boolean
    if (isBoolean(doNotResume)) {
        module.attributes.doNotResume = doNotResume;
    }
}

async function createResourceCenterModuleObject (homeView, moduleType, maxPriority) {
    const [moduleId, stepId] = await Promise.all([await createModuleId(homeView.id), await createStepId()]);

    const module = {
        id: moduleId,
        state: 'draft',
        editorType: EDITOR_TYPES.ENGAGE_UI,
        isMultiStep: false,
        supportsBuildingBlocks: true,
        audienceUiHint: {
            filters: [],
        },
        name: 'Unnamed',
        launchMethod: 'api',
        appId: homeView.appId,
        authoredLanguage: homeView.authoredLanguage,
        attributes: {
            type: 'building-block',
            sharedServiceVersion: sharedServices.version,
            priority: maxPriority,
            device: {
                type: 'desktop',
            },
            resourceCenter: {
                children: [],
                isTopLevel: false,
                // This is kind of confusing, but the moduleId property here should be set to a string that
                // represents the type of module this is. The different module types are defined in
                // BuildingBlockLayouts.moduleIds which itself has a confusing name
                moduleId: moduleType,
                // BE code is modeled in such a way that originId should point back to the _draft_ version
                // of the module
                originId: moduleId,
                homeViewId: homeView.id,
                isModule: true,
            },
        },
        steps: [
            {
                id: stepId,
                guideId: moduleId,
                attributes: {},
                templateId: 'building-block-guide',
                rank: 5000000,
            },
        ],
    };

    if (moduleType === moduleTypes.vocPortal) {
        module.attributes.resourceCenter.moduleId = moduleTypes.iframe;
        module.attributes.resourceCenter.iframeVariant = moduleTypes.vocPortal;
    }

    synchronizeModuleWithHomeView(homeView, module);

    const darkModeSelector = get(homeView, 'steps[0].attributes.darkMode.selector');
    if (darkModeSelector) {
        module.steps[0].attributes.darkMode = {
            selector: darkModeSelector,
        };
    }

    return module;
}

export function getMaxPriority (guides) {
    const maxPriority = max(
        guides.map((guide) => {
            if (guide.attributes != null && !isNaN(guide.attributes.priority) && isNumber(guide.attributes.priority)) {
                return guide.attributes.priority;
            }

            return -Infinity;
        }),
    );

    return Math.max(maxPriority, guides.length - 1);
}

function getModuleConfig (moduleType, config) {
    switch (moduleType) {
        case moduleTypes.guideList:
            return {
                title: 'Guide List Module',
                subtitle: 'Product Walkthroughs',
            };
        case moduleTypes.onboarding:
            return {
                title: 'Onboarding',
                subtitle: 'Onboarding Guides',
            };
        case moduleTypes.sandbox:
            return {
                title: 'Code Sandbox',
                subtitle: 'Custom Module',
            };
        case moduleTypes.announcements:
            return {
                title: 'Announcements',
                subtitle: "What's New",
            };
        case moduleTypes.iframe:
            return {
                title: 'External Website',
                subtitle: 'View External Content',
            };
        case moduleTypes.vocPortal:
            return {
                title: 'Listen Visitor Portal',
                subtitle: 'Listen',
            };
        case moduleTypes.integration: {
            return {
                title: config.title,
                subtitle: config.subtitle,
                integrationName: config.integrationName,
                integrationDisplayName: config.displayName,
                integrationProvider: config.integrationProvider,
            };
        }
    }
}

export function getModuleURLParam (moduleId) {
    const moduleIdDict = {
        GuideListModule: 'guide-list-module',
        OnboardingModule: 'onboarding-module',
        SandboxModule: 'sandbox-module',
        AnnouncementsModule: 'announcements-module',
        IntegrationModule: 'integration-module',
        FullyCustomModule: 'fully-custom-module',
    };

    return moduleIdDict[moduleId] || 'module';
}

export async function createModule (draftHomeView, moduleToAdd, buildingBlocksForExistingModules, maxPriority) {
    const moduleType = moduleToAdd.type ? moduleToAdd.type : moduleToAdd;
    const moduleConfig = getModuleConfig(moduleType, moduleToAdd);
    const [resourceCenterModule, draftHomeViewStepWithContent] = await Promise.all([
        await createResourceCenterModuleObject(draftHomeView, moduleType, maxPriority),
        await addStepContent(draftHomeView),
    ]);

    const buildingBlocks = JSON.parse(draftHomeViewStepWithContent.buildingBlocks);
    setModuleName(resourceCenterModule, moduleType, moduleConfig);
    setBuildingBlocksAndDom(resourceCenterModule, moduleType, buildingBlocks, buildingBlocksForExistingModules);

    if (moduleType === moduleTypes.integration) {
        await addIntegrationSandboxCode(resourceCenterModule.steps[0], moduleConfig);
    }

    // create the module on the BE
    const savedResourceCenterModule = await createResourceCenterModule(draftHomeView.id, resourceCenterModule);

    return { module: savedResourceCenterModule, moduleConfig };
}

export async function addToResourceCenter (module, moduleConfig, homeViewId) {
    // add the newly created module to the resource center
    const { subtitle, title } = moduleConfig;
    const { id, name } = module;
    let updatedResourceCenter;

    try {
        updatedResourceCenter = await addModuleToResourceCenter(homeViewId, { id, title, subtitle, name });
    } catch (error) {
        throw error;
    }

    return updatedResourceCenter;
}

export function addNotificationBubbleToResourceCenter (draftHomeView) {
    // Using cloneDeep to prevent vuex mutation error, but need to merge here to get lastUpdated and
    // content updated in state
    const draftHomeViewClone = cloneDeep(draftHomeView);

    return getGuideStepsContent(draftHomeViewClone.steps).then((stepsWithContent) => {
        setNotificationBubble(draftHomeViewClone, JSON.parse(stepsWithContent[0].buildingBlocks));

        return Promise.resolve(draftHomeViewClone);
    });
}
